import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileText, Download, Plus, Trash2, Target, TrendingUp, AlertTriangle, Shield } from 'lucide-react';
 import { invokeFunction } from '@/lib/cloudrun';
import { db } from '@/lib/firebase';
import { collection, getDocs, query, orderBy, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import html2pdf from 'html2pdf.js';

interface Competitor {
  name: string;
  marketShare: string;
  strengths: string;
  weaknesses: string;
  opportunities: string;
  threats: string;
}

interface DealOption {
  id: string;
  name: string;
  industry: string;
}

const TRANSLATIONS = {
  en: {
    title: 'Competition Report Generator',
    subtitle: 'Detailed SWOT analysis of identified competitors',
    selectDeal: 'Select a Deal',
    dealPlaceholder: 'Choose a deal to analyze',
    companyName: 'Company Name',
    industry: 'Industry',
    location: 'Location',
    competitors: 'Competitors',
    addCompetitor: 'Add Competitor',
    competitorName: 'Competitor Name',
    marketShare: 'Market Share (%)',
    strengths: 'Strengths',
    weaknesses: 'Weaknesses',
    opportunities: 'Opportunities',
    threats: 'Threats',
    generateReport: 'Generate Competition Report',
    generating: 'Generating...',
    downloadPdf: 'Download PDF',
    preview: 'Preview',
    loadingDeals: 'Loading deals...',
    noDeals: 'No deals available',
    reportGenerated: 'Competition report generated successfully',
    error: 'Error generating report',
  },
  fr: {
    title: 'Générateur de Rapport de Concurrence',
    subtitle: 'Analyse SWOT détaillée des concurrents identifiés',
    selectDeal: 'Sélectionner un Deal',
    dealPlaceholder: 'Choisir un deal à analyser',
    companyName: 'Nom de l\'entreprise',
    industry: 'Industrie',
    location: 'Localisation',
    competitors: 'Concurrents',
    addCompetitor: 'Ajouter un Concurrent',
    competitorName: 'Nom du Concurrent',
    marketShare: 'Part de Marché (%)',
    strengths: 'Forces',
    weaknesses: 'Faiblesses',
    opportunities: 'Opportunités',
    threats: 'Menaces',
    generateReport: 'Générer le Rapport de Concurrence',
    generating: 'Génération...',
    downloadPdf: 'Télécharger PDF',
    preview: 'Aperçu',
    loadingDeals: 'Chargement des deals...',
    noDeals: 'Aucun deal disponible',
    reportGenerated: 'Rapport de concurrence généré avec succès',
    error: 'Erreur lors de la génération du rapport',
  },
};

export default function CompetitionReportGenerator() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;

  const [deals, setDeals] = useState<DealOption[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [competitors, setCompetitors] = useState<Competitor[]>([
    { name: '', marketShare: '', strengths: '', weaknesses: '', opportunities: '', threats: '' }
  ]);
  const [generating, setGenerating] = useState(false);
  const [reportContent, setReportContent] = useState<any>(null);

  // Load deals from Firestore
  useEffect(() => {
    const loadDeals = async () => {
      try {
        const dealsQuery = query(collection(db, 'dealSubmissions'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(dealsQuery);
        const dealsList: DealOption[] = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().general?.tradeName || doc.data().general?.legalName || 'Unnamed Deal',
          industry: doc.data().general?.industry || 'General',
        }));
        setDeals(dealsList);
      } catch (error) {
        console.error('Error loading deals:', error);
      } finally {
        setLoadingDeals(false);
      }
    };
    loadDeals();
  }, []);

  // Load deal data when selected
  useEffect(() => {
    if (selectedDealId) {
      const selectedDeal = deals.find(d => d.id === selectedDealId);
      if (selectedDeal) {
        setCompanyName(selectedDeal.name);
        setIndustry(selectedDeal.industry);
      }
      // Load competitors from deal submission if available
      loadDealCompetitors(selectedDealId);
    }
  }, [selectedDealId, deals]);

  const loadDealCompetitors = async (dealId: string) => {
    try {
      const dealRef = doc(db, 'dealSubmissions', dealId);
      const dealSnap = await getDocs(query(collection(db, 'dealSubmissions')));
      const dealDoc = dealSnap.docs.find(d => d.id === dealId);
      
      if (dealDoc) {
        const data = dealDoc.data();
        setLocation(`${data.general?.locationCity || ''}, ${data.general?.locationCountry || ''}`);
        
        // If competitors are stored in deal data, populate them
        if (data.competitors && Array.isArray(data.competitors)) {
          setCompetitors(data.competitors.map((c: any) => ({
            name: c.name || '',
            marketShare: c.marketShare || '',
            strengths: c.strengths || '',
            weaknesses: c.weaknesses || '',
            opportunities: c.opportunities || '',
            threats: c.threats || '',
          })));
        }
      }
    } catch (error) {
      console.error('Error loading deal competitors:', error);
    }
  };

  const addCompetitor = () => {
    setCompetitors([...competitors, { name: '', marketShare: '', strengths: '', weaknesses: '', opportunities: '', threats: '' }]);
  };

  const removeCompetitor = (index: number) => {
    setCompetitors(competitors.filter((_, i) => i !== index));
  };

  const updateCompetitor = (index: number, field: keyof Competitor, value: string) => {
    const updated = [...competitors];
    updated[index][field] = value;
    setCompetitors(updated);
  };

  const generateReport = async () => {
    if (!companyName || competitors.length === 0) {
      toast({
        title: language === 'en' ? 'Missing Information' : 'Informations manquantes',
        description: language === 'en' ? 'Please fill in company name and at least one competitor' : 'Veuillez remplir le nom de l\'entreprise et au moins un concurrent',
        variant: 'destructive',
      });
      return;
    }

    setGenerating(true);
    try {
      const data = await invokeFunction('generate-market-study', {
        companyProfile: { name: companyName, industry, location, description: '',
          revenue: 0, competitors: competitors.filter(c => c.name).map(c => c.name) },
        studyType: 'competition',
        competitors: competitors.filter(c => c.name),
        dealId: selectedDealId,
      });

      // Generate enhanced report with competitor SWOT
      const enhancedReport = {
        ...data.marketStudy,
        competitorAnalysis: competitors.filter(c => c.name).map(c => ({
          name: c.name,
          marketShare: c.marketShare,
          swot: {
            strengths: c.strengths,
            weaknesses: c.weaknesses,
            opportunities: c.opportunities,
            threats: c.threats,
          },
        })),
        generatedAt: new Date().toISOString(),
      };

      setReportContent(enhancedReport);

      // Save to Firestore
      if (selectedDealId) {
        await setDoc(doc(db, 'competitionReports', selectedDealId), {
          dealId: selectedDealId,
          companyName,
          industry,
          location,
          competitors: competitors.filter(c => c.name),
          report: enhancedReport,
          status: 'draft_internal',
          generatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      toast({
        title: t.reportGenerated,
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: t.error,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = () => {
    if (!reportContent) return;

    const element = document.getElementById('competition-report-preview');
    if (element) {
      html2pdf().set({
        margin: 10,
        filename: `competition-report-${companyName.replace(/\s+/g, '-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }).from(element).save();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
        <p className="text-muted-foreground">{t.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                {t.selectDeal}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Deal Selector */}
              <div className="space-y-2">
                <Label>{t.selectDeal}</Label>
                <Select value={selectedDealId} onValueChange={setSelectedDealId}>
                  <SelectTrigger>
                    <SelectValue placeholder={loadingDeals ? t.loadingDeals : t.dealPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {deals.map((deal) => (
                      <SelectItem key={deal.id} value={deal.id}>
                        {deal.name} - {deal.industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Company Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t.companyName}</Label>
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t.industry}</Label>
                  <Input value={industry} onChange={(e) => setIndustry(e.target.value)} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t.location}</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)} />
              </div>

              {/* Competitors */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">{t.competitors}</Label>
                  <Button variant="outline" size="sm" onClick={addCompetitor}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t.addCompetitor}
                  </Button>
                </div>

                {competitors.map((competitor, index) => (
                  <Card key={index} className="p-4 bg-muted/50">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Concurrent #{index + 1}</span>
                        {competitors.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeCompetitor(index)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">{t.competitorName}</Label>
                          <Input
                            value={competitor.name}
                            onChange={(e) => updateCompetitor(index, 'name', e.target.value)}
                            placeholder="Ex: Competitor Inc."
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t.marketShare}</Label>
                          <Input
                            value={competitor.marketShare}
                            onChange={(e) => updateCompetitor(index, 'marketShare', e.target.value)}
                            placeholder="Ex: 25%"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <TrendingUp className="h-3 w-3 text-green-500" />
                            {t.strengths}
                          </Label>
                          <Textarea
                            value={competitor.strengths}
                            onChange={(e) => updateCompetitor(index, 'strengths', e.target.value)}
                            rows={2}
                            placeholder={language === 'en' ? 'Key strengths...' : 'Forces clés...'}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500" />
                            {t.weaknesses}
                          </Label>
                          <Textarea
                            value={competitor.weaknesses}
                            onChange={(e) => updateCompetitor(index, 'weaknesses', e.target.value)}
                            rows={2}
                            placeholder={language === 'en' ? 'Key weaknesses...' : 'Faiblesses clés...'}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Target className="h-3 w-3 text-blue-500" />
                            {t.opportunities}
                          </Label>
                          <Textarea
                            value={competitor.opportunities}
                            onChange={(e) => updateCompetitor(index, 'opportunities', e.target.value)}
                            rows={2}
                            placeholder={language === 'en' ? 'Opportunities...' : 'Opportunités...'}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Shield className="h-3 w-3 text-red-500" />
                            {t.threats}
                          </Label>
                          <Textarea
                            value={competitor.threats}
                            onChange={(e) => updateCompetitor(index, 'threats', e.target.value)}
                            rows={2}
                            placeholder={language === 'en' ? 'Threats...' : 'Menaces...'}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <Button 
                onClick={generateReport} 
                disabled={generating} 
                className="w-full bg-[#CEAB65] hover:bg-[#b8924f]"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t.generating}
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    {t.generateReport}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t.preview}</CardTitle>
                {reportContent && (
                  <Button onClick={downloadPdf} variant="outline" size="sm">
                    <Download className="mr-2 h-4 w-4" />
                    {t.downloadPdf}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {reportContent ? (
                <div id="competition-report-preview" className="prose prose-sm max-w-none p-4 bg-white rounded-lg max-h-[600px] overflow-y-auto">
                  <h1 className="text-xl font-bold text-[#1D314D]">
                    {language === 'en' ? 'Competition Analysis Report' : 'Rapport d\'Analyse de la Concurrence'}
                  </h1>
                  <h2 className="text-lg font-semibold text-[#2E4663]">{companyName}</h2>
                  <p className="text-sm text-gray-500">{industry} | {location}</p>
                  
                  {/* Market Overview */}
                  {reportContent.marketOverview && (
                    <div className="mt-4">
                      <h3 className="font-semibold border-b pb-1">
                        {language === 'en' ? 'Market Overview' : 'Aperçu du Marché'}
                      </h3>
                      <p className="text-sm whitespace-pre-wrap">{reportContent.marketOverview}</p>
                    </div>
                  )}

                  {/* Competitive Landscape */}
                  {reportContent.competitiveLandscape && (
                    <div className="mt-4">
                      <h3 className="font-semibold border-b pb-1">
                        {language === 'en' ? 'Competitive Landscape' : 'Paysage Concurrentiel'}
                      </h3>
                      <p className="text-sm whitespace-pre-wrap">{reportContent.competitiveLandscape}</p>
                    </div>
                  )}

                  {/* Competitor SWOT Analysis */}
                  {reportContent.competitorAnalysis && reportContent.competitorAnalysis.length > 0 && (
                    <div className="mt-4">
                      <h3 className="font-semibold border-b pb-1">
                        {language === 'en' ? 'Competitor SWOT Analysis' : 'Analyse SWOT des Concurrents'}
                      </h3>
                      {reportContent.competitorAnalysis.map((comp: any, idx: number) => (
                        <div key={idx} className="mt-3 p-3 bg-gray-50 rounded">
                          <h4 className="font-medium text-[#1D314D]">{comp.name}</h4>
                          {comp.marketShare && <p className="text-xs text-gray-500">Part de marché: {comp.marketShare}</p>}
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div className="p-2 bg-green-50 rounded">
                              <strong className="text-green-700">{t.strengths}:</strong>
                              <p>{comp.swot.strengths || '-'}</p>
                            </div>
                            <div className="p-2 bg-orange-50 rounded">
                              <strong className="text-orange-700">{t.weaknesses}:</strong>
                              <p>{comp.swot.weaknesses || '-'}</p>
                            </div>
                            <div className="p-2 bg-blue-50 rounded">
                              <strong className="text-blue-700">{t.opportunities}:</strong>
                              <p>{comp.swot.opportunities || '-'}</p>
                            </div>
                            <div className="p-2 bg-red-50 rounded">
                              <strong className="text-red-700">{t.threats}:</strong>
                              <p>{comp.swot.threats || '-'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recommendations */}
                  {reportContent.strategicRecommendations && (
                    <div className="mt-4">
                      <h3 className="font-semibold border-b pb-1">
                        {language === 'en' ? 'Strategic Recommendations' : 'Recommandations Stratégiques'}
                      </h3>
                      <p className="text-sm whitespace-pre-wrap">{reportContent.strategicRecommendations}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-[400px] flex items-center justify-center bg-muted/20 rounded-lg">
                  <p className="text-muted-foreground">
                    {language === 'en' ? 'Report preview will appear here' : 'L\'aperçu du rapport apparaîtra ici'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
